import { yupResolver } from '@hookform/resolvers/yup'
import { object } from 'yup'
import { type FieldValues, useForm, FormProvider } from 'react-hook-form'
import { isPresent } from '@jjvgaming/player-payback-library'
import { useApiClient } from 'hooks/useApiClient'
import { useSnackbar } from 'stores/useSnackbar'
import { useNavigate, Link as RouterLink } from 'react-router-dom'
import { useMutation } from '@tanstack/react-query'
import { Page } from 'components/Shared/Page'
import { PageHeader } from 'components/Shared/PageHeader'
import {
  Alert,
  Box,
  Button,
  Grid,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  type SelectChangeEvent,
  Stack,
  Typography,
} from '@mui/material'
import { TextField } from 'components/Shared/TextField'
import { FormFieldErrorMessage } from 'components/FormFieldErrorMessage'
import { useLayout } from 'hooks/useLayout'
import EventIcon from '@mui/icons-material/Event'
import AccessTimeIcon from '@mui/icons-material/AccessTime'
import { HookableDateField } from 'components/Shared/HookableDateField'
import { HookableTimeField } from 'components/Shared/HookableTimeField'
import { formatISO, parse } from 'date-fns'
import {
  badgeRewardNameSchema,
  badgeRewardDescriptionSchema,
  badgeRewardTypeSchema,
  badgeRewardRenewableTypeSchema,
  badgeRewardDependsOnBadgeRewardIdSchema,
  badgeRewardJJPointsAwardedSchema,
  badgeRewardCountSchema,
  newBadgeRewardStartDateSchema,
  newBadgeRewardStartTimeSchema,
  newBadgeRewardEndDateSchema,
  newBadgeRewardEndTimeSchema,
  badgeRewardSchedulesSchema,
  badgeRewardLimitCheckinsSchema,
} from 'utils/schemas/BadgeRewardSchema'
import { ActivityIndicator } from 'components/Shared/ActivityIndicator'
import { ScheduleRow } from 'pages/ClientManagement/LicensedEstablishments/LicensedEstablishmentNewPage/ScheduleRow'
import { daysOfWeek } from 'utils/util'
import { useState, type ElementType } from 'react'
import { useGetAllJJBadges } from 'hooks/api/Contests/BadgeReward/useGetAllJJBadges'
import {
  type BadgeRewardType,
  type BadgeRewardSchedule,
  type BadgeRewardRenewableType,
} from 'types/api'
import {
  getBadgeRewardTypeName,
  badgeRewardTypeHasAccessToControl,
} from 'utils/badgeRewardsUtils'
import { Counter } from 'components/Counter/Counter'

const resolveIconComponent = (iconSource: unknown): ElementType => {
  if (iconSource && typeof iconSource === 'object' && 'default' in iconSource) {
    return (iconSource as { default: ElementType }).default
  }

  return iconSource as ElementType
}

export const BadgeRewardSchema = object().shape({
  name: badgeRewardNameSchema,
  description: badgeRewardDescriptionSchema,
  type: badgeRewardTypeSchema,
  renewable: badgeRewardRenewableTypeSchema,
  dependsOnBadgeRewardId: badgeRewardDependsOnBadgeRewardIdSchema,
  jjPointsAwarded: badgeRewardJJPointsAwardedSchema,
  count: badgeRewardCountSchema,
  startDate: newBadgeRewardStartDateSchema.when('type', ([type], schema) =>
    requiresDateTime(type)
      ? schema.required('Must enter a start date')
      : schema.nullable().notRequired()
  ),
  startTime: newBadgeRewardStartTimeSchema.when('type', ([type], schema) =>
    requiresDateTime(type)
      ? schema.required('Must enter a start time')
      : schema.notRequired()
  ),
  endDate: newBadgeRewardEndDateSchema,
  endTime: newBadgeRewardEndTimeSchema,
  schedules: badgeRewardSchedulesSchema,
  limitCheckins: badgeRewardLimitCheckinsSchema,
})

const defaultRenewableOptions: BadgeRewardRenewableType[] = [
  'No',
  'Daily',
  'Weekly',
  'Monthly',
  'Quarterly',
  'Yearly',
]

const checkInStreakRenewableOptions: BadgeRewardRenewableType[] = [
  'No',
  'CheckInStreak',
]

const badgeTypesRequiringDateTime = new Set([
  'CheckIn',
  'CheckInStreak',
  'Referral_Count',
])

const requiresDateTime = (type: unknown) =>
  badgeTypesRequiringDateTime.has(type as string)

const getRenewableOptions = (
  type: BadgeRewardType
): BadgeRewardRenewableType[] =>
  type === 'CheckInStreak'
    ? checkInStreakRenewableOptions
    : defaultRenewableOptions

const getValidRenewableValue = (
  type: BadgeRewardType,
  renewableType?: BadgeRewardRenewableType | null
): BadgeRewardRenewableType => {
  const renewableOptions = getRenewableOptions(type)

  return renewableType && renewableOptions.includes(renewableType)
    ? renewableType
    : 'No'
}

export const JJBadgesNewPage = () => {
  const EventIconComponent = resolveIconComponent(EventIcon)
  const AccessTimeIconComponent = resolveIconComponent(AccessTimeIcon)
  const [selectedScheduleBadgeReward, setSelectedScheduleBadgeReward] =
    useState('No')
  const [limitCheckins, setLimitCheckins] = useState(false)

  const formMethods = useForm({
    resolver: yupResolver(BadgeRewardSchema),
    defaultValues: {
      limitCheckins: false,
    },
  })
  const {
    clearErrors,
    register,
    handleSubmit,
    formState: { errors },
  } = formMethods

  const { post } = useApiClient()
  const setMessage = useSnackbar((state) => state.setMessage)
  const allJJBadgesQuery = useGetAllJJBadges()
  const jjBadges = allJJBadgesQuery.data
  const navigate = useNavigate()
  const { isMobile } = useLayout()

  const [selectedBadgeRewardType, setSelectedBadgeRewardType] =
    useState<BadgeRewardType>('CheckIn')

  const [selectedBadgeRewardRenewable, setSelectedBadgeRewardRenewable] =
    useState<BadgeRewardRenewableType>('No')

  const [selectedBadgeRewardDependent, setselectedBadgeRewardDependent] =
    useState<number>(0)
  const [jjPointsAwarded, setJJPointsAwarded] = useState(0)
  const [count, setCount] = useState(0)
  const [countValidationError, setCountValidationError] = useState<
    string | null
  >(null)

  const createBadgeReward = useMutation({
    mutationKey: ['badgerewards/create'],
    mutationFn: async (data: FieldValues) => {
      const badgeRewardSchedules: BadgeRewardSchedule[] = []
      data.schedules?.forEach((schedule: BadgeRewardSchedule) => {
        if (schedule.opening && schedule.closing) {
          badgeRewardSchedules.push({
            ...schedule,
          })
        }
      })

      const returnedData = await post('/admin/badgerewards', {
        params: { header: undefined },
        body: {
          category: 'JJBadge',
          name: data.name,
          type: data.type,
          description: data.description,
          startDate: data.startDate
            ? formatISO(parse(data.startTime, 'hh:mm a', data.startDate))
            : null,
          endDate: data.endDate
            ? formatISO(parse(data.endTime, 'hh:mm a', data.endDate))
            : null,
          badgeRewardSchedules,
          renewableType: selectedBadgeRewardRenewable,
          dependentBadgeRewardId: Number(data.dependsOnBadgeRewardId) || 0,
          jjPointsAwarded,
          count,
          limitCheckins,
        },
      })
      return returnedData
    },
    onSuccess: async (returnedData) => {
      setMessage('Badge Reward was successfully created.', 'success', {
        vertical: 'top',
        horizontal: 'right',
      })
      navigate(`/Contests/JJBadges/${returnedData.data?.id as number}`)
    },
    onError: async () => {
      setMessage('Error creating badge reward', 'error', {
        vertical: 'top',
        horizontal: 'right',
      })
    },
  })

  if (createBadgeReward.isPending || allJJBadgesQuery.isPending) {
    return <ActivityIndicator />
  }

  if (createBadgeReward.isError) {
    return <h1>An error occured.</h1>
  }

  const renewableOptions = getRenewableOptions(selectedBadgeRewardType)

  const createBadgeRewardWrapper = handleSubmit((data) => {
    if (
      badgeRewardTypeHasAccessToControl(selectedBadgeRewardType, 'Count') &&
      count < 1
    ) {
      setCountValidationError('Count must be at least 1')
      return
    }

    setCountValidationError(null)
    createBadgeReward.mutate(data)
  })

  const handleTypeChange = (e: SelectChangeEvent) => {
    const nextType = e.target.value as BadgeRewardType

    clearErrors()
    setSelectedScheduleBadgeReward('No')
    setSelectedBadgeRewardType(nextType)
    setSelectedBadgeRewardRenewable((currentRenewable) =>
      getValidRenewableValue(nextType, currentRenewable)
    )
  }

  const handleRenewableChange = (e: SelectChangeEvent) => {
    setSelectedBadgeRewardRenewable(e.target.value as BadgeRewardRenewableType)
  }

  const handleDependentBadgeRewardChange = (e: SelectChangeEvent) => {
    setselectedBadgeRewardDependent(Number(e.target.value))
  }

  return (
    <Page
      header={
        <PageHeader
          title={'Create a New J&J Badge'}
          subtitle={
            'You must fill out every field in the form before clicking the submit button.\nIncomplete submissions cannot be processed.'
          }
          backText="J&J Badges"
          backPath="/Contests/JJBadges"
          isSecondary={true}
        />
      }
    >
      <FormProvider {...formMethods}>
        <form onSubmit={createBadgeRewardWrapper} noValidate>
          <Grid item xs={12} pb={1}>
            {Object.keys(errors).length > 0 && (
              <Alert severity="error">Please correct errors to continue</Alert>
            )}
          </Grid>
          <Stack spacing={4} maxWidth={isMobile ? '90vw' : undefined}>
            <Box>
              <TextField
                id="badge-reward-name-text-box"
                required
                error={isPresent(errors.name)}
                label="Name"
                placeholder="Enter badge reward name"
                {...register('name')}
              />
              <Typography variant="body-3" component={'p'} mt={1}>
                50 characters max
              </Typography>
              {errors.name?.message && (
                <FormFieldErrorMessage message={errors.name.message} />
              )}
            </Box>
            <Box
              sx={{
                maxWidth: '520px',
              }}
            >
              <Box>
                <InputLabel required sx={{ fontWeight: 'bold' }} id="type">
                  Type
                </InputLabel>
              </Box>
              <Box>
                <Select
                  fullWidth
                  defaultValue={'CheckIn'}
                  error={isPresent(errors.type)}
                  value={selectedBadgeRewardType}
                  {...register('type')}
                  onChange={handleTypeChange}
                >
                  <MenuItem value="CheckIn">Check-In</MenuItem>
                  <MenuItem value="CheckInStreak">Check-In Streak</MenuItem>
                  <MenuItem value="PatronProfile_Address">
                    Patron Profile - Address
                  </MenuItem>
                  <MenuItem value="PatronProfile_Anniversary">
                    Patron Profile - Anniversary
                  </MenuItem>
                  <MenuItem value="Referral_Count">Referral - Count</MenuItem>
                  <MenuItem value="PatronProfile_Birthday">
                    Patron Profile - Birthday
                  </MenuItem>
                </Select>
                {errors.type?.message && (
                  <FormFieldErrorMessage message={errors.type.message} />
                )}
              </Box>
            </Box>
            <Box>
              <TextField
                multiline
                rows={3}
                required
                error={isPresent(errors.description)}
                label="Description"
                placeholder="Enter description of badge reward"
                {...register('description')}
              />
              <Typography variant="body-3" component={'p'} mt={1}>
                500 characters max
              </Typography>
              {errors.description?.message && (
                <FormFieldErrorMessage message={errors.description.message} />
              )}
            </Box>

            {badgeRewardTypeHasAccessToControl(
              selectedBadgeRewardType,
              'StartDateTime'
            ) && (
              <Box sx={{ maxWidth: '520px' }}>
                <InputLabel
                  required={requiresDateTime(selectedBadgeRewardType)}
                  htmlFor="startDate"
                >
                  Start Date/Time
                </InputLabel>
                <Stack direction="row" spacing={2}>
                  <Box sx={{ flex: 1 }}>
                    <HookableDateField
                      sx={{ width: '100%' }}
                      slotProps={{
                        textField: {
                          InputProps: {
                            endAdornment: (
                              <InputAdornment position="end">
                                <EventIconComponent />
                              </InputAdornment>
                            ),
                          },
                          error: isPresent(errors.startDate),
                        },
                      }}
                      {...register('startDate')}
                    />
                    {errors.startDate?.message && (
                      <FormFieldErrorMessage
                        message={errors.startDate.message}
                      />
                    )}
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <HookableTimeField
                      {...register('startTime')}
                      slotProps={{
                        textField: {
                          InputProps: {
                            endAdornment: (
                              <InputAdornment position="end">
                                <AccessTimeIconComponent />
                              </InputAdornment>
                            ),
                          },
                          error: isPresent(errors.startTime),
                        },
                      }}
                      sx={{ width: '100%' }}
                    />
                    {errors.startTime?.message && (
                      <FormFieldErrorMessage
                        message={errors.startTime.message}
                      />
                    )}
                  </Box>
                </Stack>
              </Box>
            )}

            {badgeRewardTypeHasAccessToControl(
              selectedBadgeRewardType,
              'EndDateTime'
            ) && (
              <Box sx={{ maxWidth: '520px' }}>
                <InputLabel htmlFor="endDate">End Date/Time</InputLabel>
                <Stack direction="row" spacing={2}>
                  <Box sx={{ flex: 1 }}>
                    <HookableDateField
                      sx={{ width: '100%' }}
                      slotProps={{
                        textField: {
                          InputProps: {
                            endAdornment: (
                              <InputAdornment position="end">
                                <EventIconComponent />
                              </InputAdornment>
                            ),
                          },
                          error: isPresent(errors.endDate),
                        },
                      }}
                      {...register('endDate')}
                    />
                    {errors.endDate?.message && (
                      <FormFieldErrorMessage message={errors.endDate.message} />
                    )}
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <HookableTimeField
                      {...register('endTime')}
                      slotProps={{
                        textField: {
                          InputProps: {
                            endAdornment: (
                              <InputAdornment position="end">
                                <AccessTimeIconComponent />
                              </InputAdornment>
                            ),
                          },
                          error: isPresent(errors.endTime),
                        },
                      }}
                      sx={{ width: '100%' }}
                    />
                    {errors.endTime?.message && (
                      <FormFieldErrorMessage message={errors.endTime.message} />
                    )}
                  </Box>
                </Stack>
              </Box>
            )}

            {badgeRewardTypeHasAccessToControl(
              selectedBadgeRewardType,
              'Renewable'
            ) && (
              <Box
                sx={{
                  maxWidth: '520px',
                }}
              >
                <Box>
                  <InputLabel
                    htmlFor="renewable"
                    sx={{ fontSize: '16px', fontWeight: '100' }}
                  >
                    Renewable
                  </InputLabel>
                </Box>
                <Box>
                  <Select
                    {...register('renewable', { required: true })}
                    inputProps={{ id: 'renewableType' }}
                    error={isPresent(errors.renewable)}
                    value={selectedBadgeRewardRenewable}
                    sx={{ width: '100%', maxWidth: '520px' }}
                    onChange={handleRenewableChange}
                  >
                    {renewableOptions.map((renewableOption) => (
                      <MenuItem key={renewableOption} value={renewableOption}>
                        {renewableOption}
                      </MenuItem>
                    ))}
                  </Select>
                </Box>
              </Box>
            )}

            {badgeRewardTypeHasAccessToControl(
              selectedBadgeRewardType,
              'DependentOnBadge'
            ) && (
              <Box
                sx={{
                  maxWidth: '520px',
                }}
              >
                <Box>
                  <InputLabel
                    htmlFor="dependentBadgeRewardId"
                    sx={{ fontSize: '16px', fontWeight: '100' }}
                  >
                    Dependent on badge reward
                  </InputLabel>
                </Box>
                <Box>
                  <Select
                    {...register('dependsOnBadgeRewardId', {
                      required: false,
                    })}
                    inputProps={{ id: 'dependsOnBadgeRewardId' }}
                    error={isPresent(errors.dependsOnBadgeRewardId)}
                    value={String(selectedBadgeRewardDependent)}
                    sx={{ width: '100%', maxWidth: '520px' }}
                    onChange={handleDependentBadgeRewardChange}
                  >
                    <MenuItem value={0}>None</MenuItem>
                    {jjBadges?.map((x) => (
                      <MenuItem key={Number(x.id)} value={Number(x.id)}>
                        {x.name}
                      </MenuItem>
                    ))}
                  </Select>
                </Box>
              </Box>
            )}

            {badgeRewardTypeHasAccessToControl(
              selectedBadgeRewardType,
              'JJPointsToAward'
            ) && (
              <Box>
                <InputLabel
                  htmlFor="jjPointsAwarded"
                  sx={{ fontSize: '16px', fontWeight: '100', mb: 1 }}
                >
                  JJ Points to award on Badge earned
                </InputLabel>

                <Box sx={{ display: 'flex', justifyContent: 'flex-start' }}>
                  <Counter
                    amount={jjPointsAwarded}
                    setAmount={setJJPointsAwarded}
                    minimumAmount={0}
                  />
                </Box>
              </Box>
            )}

            {badgeRewardTypeHasAccessToControl(
              selectedBadgeRewardType,
              'Count'
            ) && (
              <Box>
                <InputLabel
                  htmlFor="count"
                  sx={{ fontSize: '16px', fontWeight: '100' }}
                >
                  {getBadgeRewardTypeName(selectedBadgeRewardType)} Count
                </InputLabel>
                <Box sx={{ display: 'flex', justifyContent: 'flex-start' }}>
                  <Counter
                    amount={count}
                    setAmount={setCount}
                    minimumAmount={0}
                  />
                </Box>
                {countValidationError && (
                  <FormFieldErrorMessage message={countValidationError} />
                )}
              </Box>
            )}

            {badgeRewardTypeHasAccessToControl(
              selectedBadgeRewardType,
              'LimitCheckins'
            ) && (
              <Stack direction="column" width={'500px'}>
                <InputLabel
                  htmlFor={'limitCheckins'}
                  sx={isMobile ? { fontSize: '16px' } : {}}
                >
                  Limit to 1 Check-in Per Day?
                </InputLabel>
                <Select
                  data-testid="limitCheckinsBoolean"
                  defaultValue="false"
                  value={limitCheckins ? 'true' : 'false'}
                  onChange={(e) => setLimitCheckins(e.target.value === 'true')}
                >
                  <MenuItem value="false">No</MenuItem>
                  <MenuItem value="true">Yes</MenuItem>
                </Select>
              </Stack>
            )}

            {badgeRewardTypeHasAccessToControl(
              selectedBadgeRewardType,
              'Schedule'
            ) && (
              <Stack direction="column" width={'500px'}>
                <InputLabel
                  htmlFor={'scheduledBoolean'}
                  sx={isMobile ? { fontSize: '16px' } : {}}
                >
                  Do you want to schedule this Badge?
                </InputLabel>
                <Select
                  data-testid="scheduledBoolean"
                  defaultValue="No"
                  value={selectedScheduleBadgeReward}
                  onChange={(e) =>
                    setSelectedScheduleBadgeReward(e.target.value)
                  }
                >
                  <MenuItem value="No">No</MenuItem>
                  <MenuItem value="Yes">Yes</MenuItem>
                </Select>
              </Stack>
            )}

            {selectedScheduleBadgeReward === 'Yes' && (
              <div>
                <Typography variant="body-2">
                  To keep the Badge reward active, don&apos;t select an end date
                  and time.
                </Typography>
                <Box className="pt-8">
                  <Typography variant="subtitle-1" component={'p'}>
                    Badge Schedule
                  </Typography>
                  <Typography variant="body-3">
                    {`Select the days you'd like the badge to be available to patrons`}
                  </Typography>
                  <Stack spacing={2} mt="20px">
                    {daysOfWeek.map((day, index) => {
                      return (
                        <Box
                          key={day}
                          style={{ height: '85px' }}
                          height={'85'}
                          className="flex flex-col justify-center"
                        >
                          <ScheduleRow
                            day={day}
                            index={index}
                            key={day}
                            defaultValues={{
                              opening: '',
                              closing: '',
                              patronCheckInPointBonusMultiplier: 0,
                            }}
                            openLabel="Enabled"
                            closedLabel="Disabled"
                          />
                        </Box>
                      )
                    })}
                  </Stack>
                </Box>
              </div>
            )}

            {isMobile && (
              <Grid container spacing={2} maxWidth="90vw">
                <Grid
                  item
                  xs={6}
                  style={{ paddingLeft: 0, paddingRight: '8px' }}
                >
                  <RouterLink to="/Contests/JJBadges">
                    <Button variant="outlined" fullWidth>
                      Cancel
                    </Button>
                  </RouterLink>
                </Grid>
                <Grid
                  item
                  xs={6}
                  style={{ paddingLeft: '8px', paddingRight: 0 }}
                >
                  <Button type="submit" variant="contained" fullWidth>
                    Create
                  </Button>
                </Grid>
              </Grid>
            )}
            {!isMobile && (
              <Stack direction="row" spacing={2}>
                <RouterLink to="/Contests/JJBadges">
                  <Button variant="outlined">Cancel</Button>
                </RouterLink>
                <Button type="submit" variant="contained">
                  Create
                </Button>
              </Stack>
            )}
          </Stack>
        </form>
      </FormProvider>
    </Page>
  )
}
